// src/components/CarouselAdmin.js

import React, { useState, useEffect, useCallback } from 'react';
import axiosInstance from '../../utils/axiosInstance';


const CarouselAdmin = () => {
    // State for managing slides, form inputs, and UI feedback
    const [slides, setSlides] = useState([]);
    const [title, setTitle] = useState('');
    const [imageFile, setImageFile] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);

    // Function to fetch all carousel slides from the API
    const fetchSlides = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await axiosInstance.get(`/carousel`)
            console.log(response,"response")
          
            setSlides(response.data);
        } catch (err) {
            setError('Failed to fetch slides. Please make sure the server is running.');
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Fetch slides when the component first mounts
    useEffect(() => {
        fetchSlides();
    }, [fetchSlides]);

  
    const handleCreateSlide = async (e) => {
        e.preventDefault();
        
        if (!imageFile) {
            setError('Please select an image file to upload.');
            return;
        }

        setIsLoading(true);
        setError(null);
        setSuccessMessage(null);

        // FormData is required for file uploads
        const formData = new FormData();
        formData.append('title', title);
        formData.append('image', imageFile);

      

        try {
            
            const response = await axiosInstance.post('/carousel', formData)
            console.log('response', response)
            setSuccessMessage(response.data.message);
            setTitle(''); // Reset form
            setImageFile(null);
            e.target.reset(); // Clears the file input
            fetchSlides(); // Refresh the list of slides

        } catch (err) {
            setError(err.message);
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    };

    // Handles the deletion of a slide
    const handleDeleteSlide = async (id) => {
        if (!window.confirm('Are you sure you want to delete this slide?')) {
            return;
        }

        setIsLoading(true);
        setError(null);
        setSuccessMessage(null);
        
      
        try {
           
            const response = await axiosInstance.delete(`/carousel/${id}`)
console.log("Delete", response)
            setSuccessMessage(response.data.message || "Carousel Deleted Successfully !");
            fetchSlides(); // Refresh the list of slides

        } catch (err) {
            setError(err.message);
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="max-w-5xl mx-auto my-8 p-4">
            <h2 className="text-3xl font-bold text-gray-800 mb-6 pb-2 border-b-2 border-gray-200">
                Manage Carousel 🎠
            </h2>

            {/* --- FORM FOR CREATING A NEW SLIDE --- */}
            <div className="bg-white p-6 rounded-lg shadow-md mb-8">
                <h3 className="text-xl font-semibold text-gray-700 mb-4">Add New Slide</h3>
                <form onSubmit={handleCreateSlide} className="space-y-4">
                    <div>
                        <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                            Slide Title
                        </label>
                        <input
                            type="text"
                            id="title"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="e.g., Special Summer Sale"
                            required
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>
                    <div>
                        <label htmlFor="image" className="block text-sm font-medium text-gray-700 mb-1">
                            Slide Image
                        </label>
                        <input
                            type="file"
                            id="image"
                            accept="image/png, image/jpeg, image/jpg, image/gif, image/webp"
                            onChange={(e) => setImageFile(e.target.files[0])}
                            required
                            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                        />
                         <p className="mt-1 text-xs text-gray-500">Allowed types: JPG, PNG, GIF, WEBP. Max size: 5MB.</p>
                    </div>
                    <button 
                        type="submit" 
                        disabled={isLoading}
                        className="w-full sm:w-auto px-6 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                    >
                        {isLoading ? 'Uploading...' : 'Add Slide'}
                    </button>
                </form>
            </div>

            {/* --- UI FEEDBACK MESSAGES --- */}
            {error && <div className="p-4 mb-6 text-center text-red-800 bg-red-100 rounded-md">{error}</div>}
            {successMessage && <div className="p-4 mb-6 text-center text-green-800 bg-green-100 rounded-md">{successMessage}</div>}

            {/* --- DISPLAY CURRENT SLIDES --- */}
            <div>
                <h3 className="text-xl font-semibold text-gray-700 mb-4">Current Slides</h3>
                {isLoading && slides.length === 0 && <p className="text-gray-500">Loading slides...</p>}
                {!isLoading && slides.length === 0 && <p className="text-gray-500">No slides found. Add one using the form above.</p>}
                
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {slides.map((slide) => (
                        <div key={slide.id} className="bg-white rounded-lg shadow-md overflow-hidden flex flex-col">
                            <img src={slide.image_url} alt={slide.title} className="w-full h-40 object-cover" />
                            <div className="p-4 flex justify-between items-center flex-grow">
                                <p className="font-medium text-gray-800 break-words pr-2">{slide.title}</p>
                                <button 
                                    onClick={() => handleDeleteSlide(slide.id)} 
                                    disabled={isLoading}
                                    className="flex-shrink-0 px-3 py-1 text-sm text-white bg-red-500 rounded-md hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:bg-gray-400 transition-colors"
                                >
                                    Delete
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default CarouselAdmin;